import type { Chiave, Traduttore } from "../../i18n/index.ts";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  formattaDurata,
  raggruppaPerFase,
  reazioneA,
  type FaseSvolta,
  type Gruppo,
  type Riga,
} from "../lib/esecuzione.ts";
import { testoErrore, useT } from "../lib/i18n.tsx";
import { livelloDi, segmentaAnsi } from "../lib/log-ansi.ts";
import { streamEvents } from "../lib/sse.ts";
import type { DeployEvent } from "../lib/types.ts";
import { STILE_LIVELLO, TestoAnsi } from "./TestoLog.tsx";
import { Button, Spinner, cx } from "./ui.tsx";

/**
 * Console di avanzamento di un'operazione.
 *
 * È la risposta al secondo problema per cui Portman esiste: durante un deploy
 * non si vede nulla. Qui ogni riga compare mentre accade, distinguendo ciò che
 * dice Portman da ciò che dice Docker.
 *
 * Sta ancorata al fondo della finestra invece che dentro la pagina per due
 * ragioni pratiche: cresceva di riga in riga rimpicciolendo gli editor sotto di
 * sé — la pagina ballava per tutto il deploy — e restava in cima al dettaglio
 * dello stack, cioè fuori vista proprio a chi aveva appena avviato un
 * ripristino dallo storico, in fondo alla schermata.
 */

type Esito = "in-corso" | "riuscito" | "fallito";

/** Cosa si sta facendo allo stack, dichiarato dal server all'avvio. */
type AzioneOperazione = Extract<DeployEvent, { type: "started" }>["action"];

export interface EsecuzioneDeploy {
  righe: Riga[];
  /** Le fasi già annunciate, in ordine: l'ultima è quella in corso. */
  fasi: FaseSvolta[];
  /** `null` finché il server non dice di quale operazione si tratta. */
  azione: AzioneOperazione | null;
  /** Chi ha avviato l'operazione: può essere un altro utente, o un webhook. */
  attore: string | null;
  esito: Esito | null;
  errore: string | null;
  durataMs: number | null;
  /** Il collegamento ai log è caduto e si sta tornando a seguire l'operazione. */
  riconnessione: boolean;
  avvia: (path: string) => Promise<void>;
  /** Si riaggancia a un'operazione già in corso, senza avviarne una nuova. */
  riaggancia: (path: string) => Promise<void>;
  pulisci: () => void;
}

/** Chiude la fase aperta: da qui in poi il tempo è di quella successiva. */
const concludi = (fasi: FaseSvolta[], quando: number): FaseSvolta[] =>
  fasi.map((fase, i) => (i === fasi.length - 1 && fase.fine === null ? { ...fase, fine: quando } : fase));

/**
 * Gestisce il flusso di eventi di un'operazione e lo stato che ne deriva.
 *
 * `pathRiaggancio` serve quando il collegamento cade a metà: l'operazione
 * prosegue nel server, e senza un modo di tornare a seguirla l'unica cosa che
 * l'interfaccia potrebbe fare è dichiarare un fallimento che non è avvenuto.
 */
export function useEsecuzione(
  onCompletata?: () => void,
  pathRiaggancio?: string,
): EsecuzioneDeploy {
  const [righe, setRighe] = useState<Riga[]>([]);
  const [fasi, setFasi] = useState<FaseSvolta[]>([]);
  const [azione, setAzione] = useState<AzioneOperazione | null>(null);
  const [attore, setAttore] = useState<string | null>(null);
  const [esito, setEsito] = useState<Esito | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [durataMs, setDurataMs] = useState<number | null>(null);
  const [riconnessione, setRiconnessione] = useState(false);
  // Un riferimento, non una dipendenza: cambiare lingua non deve riagganciare il flusso.
  const traduttore = useT();
  const t = useRef(traduttore);
  t.current = traduttore;

  const segui = useCallback(
    async (path: string, method: "POST" | "GET", riconnettendo = false) => {
      // Le righe si azzerano anche riagganciandosi: il server ripete gli eventi
      // dall'inizio, quindi tenerle le duplicherebbe.
      setRighe([]);
      setFasi([]);
      setAzione(null);
      setAttore(null);
      setErrore(null);
      setDurataMs(null);
      setEsito("in-corso");
      setRiconnessione(riconnettendo);

      let eventiRicevuti = 0;
      let esitoNoto = false;
      // La fase vive anche qui, e non solo nello stato: ogni riga va attribuita
      // alla fase in corso nell'istante in cui arriva, e lo stato di React è in
      // ritardo di un render sul flusso che sta scorrendo.
      let faseCorrente = 0;
      // Lo stack era già impegnato da un'altra operazione: la si segue invece di dirla fallita.
      let occupato = false;

      try {
        for await (const { data } of streamEvents<DeployEvent>(path, { method })) {
          // Il collegamento è ristabilito: gli eventi tornano ad arrivare.
          if (eventiRicevuti === 0 && riconnettendo) setRiconnessione(false);
          eventiRicevuti += 1;

          if (data.type === "log") {
            const fase = faseCorrente;
            setRighe((precedenti) => [...precedenti, { stream: data.stream, message: data.message, fase }]);
          } else if (data.type === "started") {
            setAzione(data.action);
            setAttore(data.actor);
          } else if (data.type === "fase") {
            faseCorrente = data.indice;
            const adesso = Date.now();

            setFasi((precedenti) => [
              ...concludi(precedenti, adesso),
              {
                titolo: t.current.traduci({ chiave: data.testo }) ?? data.testo,
                indice: data.indice,
                totale: data.totale,
                inizio: adesso,
                fine: null,
              },
            ]);
          } else if (data.type === "finished") {
            esitoNoto = true;
            setFasi((precedenti) => concludi(precedenti, Date.now()));
            setEsito("riuscito");
            setDurataMs(data.durationMs);
          } else if (data.type === "failed" && data.occupato && pathRiaggancio) {
            occupato = true;
          } else if (data.type === "failed") {
            esitoNoto = true;
            setFasi((precedenti) => concludi(precedenti, Date.now()));
            setEsito("fallito");
            setErrore(data.error);
          }
        }

        if (occupato) {
          void segui(pathRiaggancio!, "GET");
          return;
        }

        // Nessun evento: non c'era alcuna operazione da seguire. La console
        // torna invisibile invece di mostrare un riquadro vuoto.
        if (eventiRicevuti === 0) setEsito(null);
        return;
      } catch (cause) {
        const reazione = reazioneA({ esitoNoto, eventiRicevuti, errore: cause });

        if (reazione.tipo === "fallimento") {
          setEsito("fallito");
          setErrore(testoErrore(cause, t.current));
          return;
        }

        if (reazione.tipo === "ignora") return;

        // Un solo tentativo: se cade anche il riaggancio, insistere non
        // aggiungerebbe informazione e terrebbe la schermata in attesa.
        if (pathRiaggancio && !riconnettendo) {
          void segui(pathRiaggancio, "GET", true);
          return;
        }

        // L'operazione prosegue nel server ma non la stiamo più seguendo:
        // dichiararla fallita sarebbe falso, dirla riuscita sarebbe azzardato.
        setEsito(null);
        setRiconnessione(false);
      } finally {
        onCompletata?.();
      }
    },
    [onCompletata, pathRiaggancio],
  );

  const avvia = useCallback((path: string) => segui(path, "POST"), [segui]);

  /** Si riaggancia a un'operazione già avviata, dopo aver ricaricato la pagina. */
  const riaggancia = useCallback((path: string) => segui(path, "GET"), [segui]);

  return {
    righe,
    fasi,
    azione,
    attore,
    esito,
    errore,
    durataMs,
    riconnessione,
    avvia,
    riaggancia,
    pulisci: useCallback(() => {
      setRighe([]);
      setFasi([]);
      setAzione(null);
      setAttore(null);
      setEsito(null);
      setErrore(null);
    }, []),
  };
}

/*
 * Tre voci, tre colori.
 *
 * `stderr` non è la voce degli errori: docker compose ci scrive l'avanzamento
 * normale — «Pulling», «Pulled», «Created» — e dipingerlo di rosso farebbe
 * sembrare guasto ogni rilascio riuscito. Resta distinto dal resto, senza
 * allarme: il fallimento vero ha il suo riquadro, in fondo.
 */
const COLORI: Record<Riga["stream"], string> = {
  portman: "text-accent-500",
  stderr: "text-amber-200/75",
  stdout: "text-neutral-300",
};

/** Solo la voce di Portman si annuncia: è l'unica che non viene da Docker. */
const MARCATORI: Record<Riga["stream"], string> = {
  portman: "▸",
  stderr: "",
  stdout: "",
};

export const NOMI_OPERAZIONE: Record<AzioneOperazione, Chiave> = {
  deploy: "console.rilascio",
  stop: "console.arresto",
  restart: "console.riavvio",
  pull: "console.pull",
  rename: "console.rinomina",
};

/**
 * Ritmo del cronometro.
 *
 * Il tempo trascorso è l'unica misura onesta durante un'attesa: un pull non
 * emette righe per decine di secondi, e un numero che sale dice che il silenzio
 * non è un blocco. Si aggiorna solo mentre l'operazione è viva.
 */
function useCronometro(attivo: boolean) {
  const [, battito] = useState(0);

  useEffect(() => {
    if (!attivo) return;

    const orologio = setInterval(() => battito((n) => n + 1), 1000);
    return () => clearInterval(orologio);
  }, [attivo]);
}

/** Lo stato di una fase si legge dalla sua posizione e dall'esito complessivo. */
function statoDella(
  fase: FaseSvolta,
  fasi: readonly FaseSvolta[],
  esito: Esito,
): "conclusa" | "corrente" | "fallita" {
  if (fase.indice !== fasi[fasi.length - 1]?.indice) return "conclusa";
  if (esito === "fallito") return "fallita";
  if (esito === "in-corso") return "corrente";

  return "conclusa";
}

/** Il messaggio senza sequenze ANSI, come lo si legge. */
const testoPulito = (message: string) =>
  segmentaAnsi(message)
    .map((s) => s.text)
    .join("");

/** I log come si incollano in una segnalazione: con le fasi che li separano. */
const testoDeiLog = (gruppi: readonly Gruppo[], t: Traduttore): string =>
  gruppi
    .map(({ fase, righe }) => {
      const corpo = righe.map((riga) => testoPulito(riga.message)).join("\n");
      if (!fase) return corpo;

      const durata = fase.fine === null ? t.t("console.inCorso") : formattaDurata(fase.fine - fase.inizio);
      const intestazione = t.t("console.faseNeiLog", {
        indice: fase.indice,
        totale: fase.totale,
        titolo: fase.titolo,
        durata,
      });
      return `── ${intestazione}\n${corpo}`;
    })
    .join("\n");

export function DeployConsole({
  esecuzione,
  onAnnulla,
}: {
  esecuzione: EsecuzioneDeploy;
  /** Assente per chi non ha il permesso di avviare operazioni. */
  onAnnulla?: () => Promise<void> | void;
}) {
  const pannello = useRef<HTMLDivElement>(null);
  const riquadro = useRef<HTMLDivElement>(null);
  const [aperta, setAperta] = useState(true);
  const [seguiFondo, setSeguiFondo] = useState(true);
  const [annullando, setAnnullando] = useState(false);
  const [copiato, setCopiato] = useState(false);
  const t = useT();
  /** Le fasi che chi guarda ha aperto o chiuso a mano, contro il comportamento
      automatico: una scelta esplicita vale più della regola. */
  const [forzate, setForzate] = useState<Record<number, boolean>>({});

  const { esito, fasi, righe, errore, durataMs } = esecuzione;
  const inCorso = esito === "in-corso";

  useCronometro(inCorso);

  const gruppi = useMemo(() => raggruppaPerFase(righe, fasi), [righe, fasi]);

  // A operazione nuova la console si apre e dimentica le fasi aperte a mano;
  // finita bene si richiude da sé, perché un elenco di successi non si legge;
  // finita male resta aperta, che è l'unico momento in cui quelle righe servono.
  useEffect(() => {
    if (esito === "in-corso") {
      setAnnullando(false);
      setForzate({});
      setAperta(true);
    }

    if (esito === "riuscito") setAperta(false);
    if (esito === "fallito") setAperta(true);
  }, [esito]);

  /**
   * Lo spazio occupato in fondo alla finestra, dichiarato al resto della pagina.
   *
   * Gli editor crescono fino al bordo inferiore (vedi lib/altezza.ts): senza
   * questa misura finirebbero sotto al pannello, e l'ultima riga di YAML
   * resterebbe illeggibile proprio mentre si guarda un deploy fallire.
   */
  useEffect(() => {
    const radice = document.documentElement;

    const dimentica = () => {
      radice.style.removeProperty("--spazio-console");
      window.dispatchEvent(new Event("resize"));
    };

    const elemento = pannello.current;
    if (!elemento) {
      dimentica();
      return;
    }

    const misura = () => {
      radice.style.setProperty("--spazio-console", `${elemento.offsetHeight}px`);
      // Chi misura lo spazio disponibile ascolta il ridimensionamento della
      // finestra: un pannello che compare è, per lui, la stessa cosa.
      window.dispatchEvent(new Event("resize"));
    };

    misura();

    // La misura rimbalza al fotogramma successivo: farla dentro la notifica
    // dell'osservatore riaprirebbe un giro di layout nello stesso ciclo, e il
    // browser lo segnala come «ResizeObserver loop» — un errore in faccia a chi
    // sviluppa, per un riquadro che si è solo alzato di due pixel.
    let prossimoFotogramma = 0;
    const osservatore = new ResizeObserver(() => {
      cancelAnimationFrame(prossimoFotogramma);
      prossimoFotogramma = requestAnimationFrame(misura);
    });
    osservatore.observe(elemento);

    return () => {
      cancelAnimationFrame(prossimoFotogramma);
      osservatore.disconnect();
      dimentica();
    };
  }, [esito]);

  // Segue le nuove righe, ma smette se l'utente scorre indietro per leggere:
  // riportarlo in fondo mentre cerca un errore sarebbe fastidioso.
  //
  // Lo scorrimento lo impone il contenitore, non un elemento sentinella: un
  // `scrollIntoView` su un div alto zero non fa nulla se il browser lo giudica
  // già visibile — e con il padding in fondo capita spesso, mentre l'ultima
  // riga è per metà fuori. Era questo a fermare la console a un dito dal fondo
  // finché non si scorreva a mano.
  useEffect(() => {
    if (!seguiFondo) return;

    const el = riquadro.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [righe.length, errore, seguiFondo, aperta]);

  if (esito === null) return null;

  const corrente = fasi[fasi.length - 1] ?? null;
  const trascorso = corrente === null ? null : Date.now() - (fasi[0]?.inizio ?? Date.now());

  return (
    <div
      ref={pannello}
      className="fixed inset-x-0 bottom-0 z-30 border-t bg-[var(--superficie-alta)]"
      style={{ boxShadow: "0 -6px 24px oklch(0 0 0 / 0.14)" }}
    >
      {/* Segno di vita durante i silenzi lunghi: una posizione non la dichiara,
          perché le fasi durano tempi incomparabili e nessuno sa quanto manchi. */}
      {inCorso && (
        <div className="absolute inset-x-0 top-0 h-0.5 overflow-hidden" aria-hidden>
          <div className="scorre h-full w-1/3 bg-accent-500" />
        </div>
      )}

      <div className="contenitore flex min-h-12 flex-wrap items-center gap-x-3 gap-y-1 py-2">
        <SegnoEsito esito={esito} />

        {/* Il cambio di fase va annunciato, non solo mostrato: senza questo chi
            usa uno screen reader non saprebbe nulla dell'avanzamento — proprio
            ciò che questa riga esiste per dire. */}
        <p className="flex min-w-0 flex-wrap items-baseline gap-x-2 text-sm" role="status">
          <span className="font-medium">
            {t.t(esecuzione.azione === null ? "console.operazione" : NOMI_OPERAZIONE[esecuzione.azione])}
            {esito === "riuscito" && ` ${t.t("console.completato")}`}
            {esito === "fallito" && ` ${t.t("console.fallito")}`}
          </span>

          {inCorso && corrente && (
            <span className="tenue">
              {t.t("console.fase", { indice: corrente.indice, totale: corrente.totale, titolo: corrente.titolo })}
            </span>
          )}

          {esito === "riuscito" && durataMs !== null && (
            <span className="tenue tabular-nums">{t.t("console.in", { durata: formattaDurata(durataMs) })}</span>
          )}

          {inCorso && trascorso !== null && (
            <span className="tenue tabular-nums">{formattaDurata(trascorso)}</span>
          )}
        </p>

        {inCorso && corrente && (
          <div className="flex items-center gap-1.5" aria-hidden>
            {Array.from({ length: corrente.totale }, (_, i) => (
              <span
                key={i}
                className={cx(
                  "size-1.5 rounded-full transition-colors",
                  i < corrente.indice ? "bg-accent-500" : "bg-[var(--bordo)]",
                )}
              />
            ))}
          </div>
        )}

        {esecuzione.riconnessione && (
          <span className="text-xs tenue">{t.t("console.riconnessione")}</span>
        )}

        <div className="ml-auto flex items-center gap-1">
          {righe.length > 0 && (
            <Button
              variant="ghost"
              className="text-xs"
              onClick={async () => {
                await navigator.clipboard.writeText(testoDeiLog(gruppi, t));
                setCopiato(true);
                setTimeout(() => setCopiato(false), 2000);
              }}
            >
              {t.t(copiato ? "comune.copiato" : "comune.copia")}
            </Button>
          )}

          {onAnnulla && inCorso && (
            <Button
              variant="ghost"
              className="text-xs"
              loading={annullando}
              onClick={async () => {
                setAnnullando(true);

                try {
                  await onAnnulla();
                } catch {
                  // Se la richiesta non passa il bottone torna premibile: la sola
                  // cosa peggiore di un annullamento non riuscito è non poterlo
                  // ritentare.
                  setAnnullando(false);
                }
              }}
            >
              {t.t(annullando ? "console.annullo" : "comune.annulla")}
            </Button>
          )}

          <Button
            variant="ghost"
            className="text-xs"
            aria-expanded={aperta}
            onClick={() => setAperta((precedente) => !precedente)}
          >
            {aperta ? t.t("console.riduci") : t.t("console.mostraLog", { righe: righe.length })}
            <Freccia verso={aperta ? "giù" : "su"} />
          </Button>

          {/* Chiudere sgombera la finestra; l'operazione, se è in corso, prosegue
              nel server e si ritrova riaprendo lo stack. */}
          <Button variant="ghost" className="text-xs" onClick={esecuzione.pulisci}>
            {t.t("comune.chiudi")}
          </Button>
        </div>
      </div>

      {aperta && (
        <div className="contenitore pb-3">
          <div
            ref={riquadro}
            onScroll={(event) => {
              const el = event.currentTarget;
              setSeguiFondo(el.scrollHeight - el.scrollTop - el.clientHeight < 40);
            }}
            className="console corridoio-barra max-h-[min(40vh,22rem)] overflow-y-auto rounded-xl py-1.5 font-mono text-[13px] leading-relaxed"
          >
            {gruppi.map((gruppo) => {
              const stato = gruppo.fase && statoDella(gruppo.fase, fasi, esito);

              return (
                <SezioneFase
                  key={gruppo.fase ? gruppo.fase.indice : "preludio"}
                  gruppo={gruppo}
                  stato={stato}
                  aperta={
                    gruppo.fase === null ||
                    (forzate[gruppo.fase.indice] ?? (stato === "corrente" || stato === "fallita"))
                  }
                  onToggle={() =>
                    gruppo.fase &&
                    setForzate((precedenti) => ({
                      ...precedenti,
                      [gruppo.fase!.indice]: !(
                        precedenti[gruppo.fase!.indice] ??
                        (stato === "corrente" || stato === "fallita")
                      ),
                    }))
                  }
                />
              );
            })}

            {righe.length === 0 && !errore && (
              <p className="px-4 py-2 text-neutral-500">{t.t("console.attesaRighe")}</p>
            )}

            {errore && (
              <p className="mx-4 my-2 whitespace-pre-wrap rounded-lg bg-red-500/10 px-3 py-2 text-rose-300">
                {errore}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Una fase con le sue righe.
 *
 * Le fasi concluse restano chiuse: dopo un rilascio riuscito nessuno rilegge lo
 * scaricamento delle immagini, ma la sua durata — quella sì — dice dove se ne va
 * il tempo, e resta in vista senza aprire nulla.
 */
function SezioneFase({
  gruppo,
  stato,
  aperta,
  onToggle,
}: {
  gruppo: Gruppo;
  stato: "conclusa" | "corrente" | "fallita" | null;
  aperta: boolean;
  onToggle: () => void;
}) {
  const { fase, righe } = gruppo;

  return (
    <section>
      {fase && (
        <button
          type="button"
          onClick={onToggle}
          disabled={righe.length === 0}
          aria-expanded={aperta}
          className="console-fase sticky top-0 z-10 flex w-full items-center gap-2.5 px-4 py-1.5 text-left font-sans text-xs disabled:cursor-default"
        >
          <SegnoFase stato={stato} />
          <span className="tabular-nums text-neutral-500">
            {fase.indice}/{fase.totale}
          </span>
          <span className={cx("truncate", stato === "conclusa" ? "text-neutral-400" : "text-neutral-100")}>
            {fase.titolo}
          </span>
          <span className="ml-auto flex shrink-0 items-center gap-2 tabular-nums text-neutral-500">
            {formattaDurata((fase.fine ?? Date.now()) - fase.inizio)}
            {righe.length > 0 && <Freccia verso={aperta ? "su" : "giù"} />}
          </span>
        </button>
      )}

      {aperta &&
        righe.map((riga, indice) => {
          const segmenti = segmentaAnsi(riga.message);
          // Solo errori e avvisi si marcano: l'avanzamento di compose è già
          // distinto dal colore della sua voce, e un bordo su ogni riga sarebbe rumore.
          const livello = riga.stream === "portman" ? null : livelloDi(segmenti.map((s) => s.text).join(""));
          const marcato = livello === "errore" || livello === "avviso";

          return (
            <div
              key={indice}
              className={cx("flex gap-2 border-l-2 pr-4 pl-3.5", marcato ? STILE_LIVELLO[livello] : "border-transparent")}
            >
              <span aria-hidden className={cx("w-2 shrink-0 select-none", COLORI[riga.stream])}>
                {MARCATORI[riga.stream]}
              </span>
              <span
                className={cx("min-w-0 flex-1 whitespace-pre-wrap break-words", !marcato && COLORI[riga.stream])}
              >
                <TestoAnsi segmenti={segmenti} />
              </span>
            </div>
          );
        })}
    </section>
  );
}

function SegnoEsito({ esito }: { esito: Esito }) {
  if (esito === "in-corso") return <Spinner className="size-4 text-accent-500" />;

  if (esito === "riuscito") {
    return (
      <span className="flex size-4 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
        <Spunta />
      </span>
    );
  }

  return (
    <span className="flex size-4 items-center justify-center rounded-full bg-red-500/15 text-red-600 dark:text-red-400">
      <Croce />
    </span>
  );
}

function SegnoFase({ stato }: { stato: "conclusa" | "corrente" | "fallita" | null }) {
  if (stato === "corrente") return <Spinner className="size-3 text-accent-500" />;
  if (stato === "fallita") return <span className="text-rose-400"><Croce /></span>;

  return <span className="text-emerald-400/80"><Spunta /></span>;
}

const Spunta = () => (
  <svg viewBox="0 0 16 16" className="size-3" fill="none" aria-hidden>
    <path d="M3.5 8.5 6.5 11.5 12.5 4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Croce = () => (
  <svg viewBox="0 0 16 16" className="size-3" fill="none" aria-hidden>
    <path d="M4 4 12 12M12 4 4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const Freccia = ({ verso }: { verso: "su" | "giù" }) => (
  <svg viewBox="0 0 16 16" className="size-3 opacity-70" fill="none" aria-hidden>
    <path
      d={verso === "su" ? "M3.5 10 8 5.5 12.5 10" : "M3.5 6 8 10.5 12.5 6"}
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
